import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowLeft, ArrowRight, Clock, Lock, Sparkles, Zap } from "lucide-react";
import { API } from "./api";
import AILoader from "./components/AILoader";
import SuccessScreen from "./components/SuccessScreen";
import ReportDashboard from "./components/ReportDashboard";
import { mapAIReport } from "./components/ReportMapper";
import {
  ContactStep,
  IndustryStep,
  QuestionsStep,
  ChallengesStep,
  OTHER,
  MIN_CHALLENGE_CHARS
} from "./components/FormSteps";

const STORAGE_KEY = "ai-growth-scan-v2";
const LEGACY_STORAGE_KEY = "ai-growth-scan";
const INDUSTRY_SECTION = "Industry Focus";

const EMPTY_COMPANY = {
  companyName: "",
  name: "",
  email: "",
  mobile: "",
  website: "",
  industry: "",
  industryLabel: "",
  industryOther: "",
  challenges: ""
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i;

const loadSaved = () => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const persist = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable (private mode); the app still works without it */
  }
};

const clearSaved = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
};

const isAnswered = (q, value, otherText) => {
  if (q.type === "multi") {
    if (!Array.isArray(value) || value.length === 0) return "Please select at least one option";
    if (value.includes(OTHER) && !otherText?.trim()) return "Please specify 'Other'";
    return null;
  }
  if (!value) return "Please select an option";
  if (value === OTHER && !otherText?.trim()) return "Please specify 'Other'";
  return null;
};

const resolveAnswer = (q, value, otherText) => {
  const swap = v => (v === OTHER ? `Other: ${otherText?.trim() || ""}` : v);
  return Array.isArray(value) ? value.map(swap) : swap(value);
};

export default function App() {
  const saved = useRef(loadSaved()).current;

  const [company, setCompany] = useState({ ...EMPTY_COMPANY, ...(saved?.company || {}) });
  const [answers, setAnswers] = useState(saved?.answers || {});
  const [otherInputs, setOtherInputs] = useState(saved?.otherInputs || {});
  const [step, setStep] = useState(saved?.step || 0);
  const [report, setReport] = useState(saved?.report || null);
  const [stage, setStage] = useState(saved?.report ? "report" : "form"); // form | loading | success | report

  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  // -----------------------------
  // DATA LOADING
  // -----------------------------
  useEffect(() => {
    API.get("/questions/industries")
      .then(res => setIndustries(res.data || []))
      .catch(() => setLoadError("We couldn't load industries. Please refresh the page."))
      .finally(() => setIndustriesLoading(false));
  }, []);

  useEffect(() => {
    if (!company.industry) {
      setQuestions([]);
      return;
    }

    let cancelled = false;
    setQuestionsLoading(true);

    API.get("/questions", { params: { industry: company.industry } })
      .then(res => {
        if (!cancelled) {
          setQuestions(res.data || []);
          setLoadError(null);
        }
      })
      .catch(() => !cancelled && setLoadError("We couldn't load the questionnaire. Please refresh the page."))
      .finally(() => !cancelled && setQuestionsLoading(false));

    return () => { cancelled = true; };
  }, [company.industry]);

  // -----------------------------
  // PERSISTENCE (debounced)
  // -----------------------------
  // No setState here: effects from input events flush synchronously, and a state update per
  // keystroke can trip React's nested-update limit during fast input (autofill, paste bursts).
  useEffect(() => {
    const t = setTimeout(() => {
      persist({ company, answers, otherInputs, step, report: stage === "report" || stage === "success" ? report : null });
    }, 300);
    return () => clearTimeout(t);
  }, [company, answers, otherInputs, step, report, stage]);

  // -----------------------------
  // STEPS
  // -----------------------------
  const industryLabel = company.industry === "other"
    ? (company.industryOther || "Your Industry")
    : company.industryLabel;

  const sectionGroups = useMemo(() => {
    const groups = [];
    questions.forEach(q => {
      let g = groups.find(x => x.section === q.section);
      if (!g) {
        g = { section: q.section, questions: [] };
        groups.push(g);
      }
      g.questions.push(q);
    });
    return groups;
  }, [questions]);

  const steps = useMemo(() => {
    const list = [
      { id: "contact", title: "Your Details", eyebrow: "Let's get started", subtitle: "Tell us who you are so we can personalise and send your report." },
      { id: "industry", title: "Your Industry", eyebrow: "Industry", subtitle: "We'll tailor every question and recommendation to your industry." }
    ];

    let number = 1;
    sectionGroups.forEach(g => {
      const isIndustry = g.section === INDUSTRY_SECTION;
      list.push({
        id: `section:${g.section}`,
        title: isIndustry ? `${industryLabel || "Industry"} Focus` : g.section,
        eyebrow: isIndustry ? "Industry deep-dive" : "Assessment",
        subtitle: isIndustry
          ? `A few questions specific to ${industryLabel || "your industry"}.`
          : "Pick the option that best describes your business today.",
        section: g.section,
        questions: g.questions,
        startNumber: number
      });
      number += g.questions.length;
    });

    list.push({
      id: "challenges",
      title: "Your Challenges",
      eyebrow: "Final step",
      subtitle: "Describe what's holding your business back. This shapes your recommendations."
    });
    return list;
  }, [sectionGroups, industryLabel]);

  // A restored session may point past the steps that exist until its questions have loaded.
  const waitingForQuestions = Boolean(company.industry) && questions.length === 0 && !loadError && step > 1;
  const safeStep = waitingForQuestions ? 1 : Math.min(step, steps.length - 1);
  const current = steps[safeStep];

  // -----------------------------
  // VALIDATION (per step)
  // -----------------------------
  const errorsFor = useCallback((s) => {
    const e = {};
    if (!s) return e;

    if (s.id === "contact") {
      if (!company.companyName.trim()) e.companyName = "Company name is required";
      if (!company.name.trim()) e.name = "Your name is required";
      if (!company.email.trim()) e.email = "Email is required";
      else if (!EMAIL_RE.test(company.email.trim())) e.email = "Enter a valid email address";
      const digits = company.mobile.replace(/\D/g, "");
      if (!company.mobile.trim()) e.mobile = "Mobile number is required";
      else if (digits.length < 8 || digits.length > 15) e.mobile = "Enter a valid mobile number";
      if (company.website.trim() && !URL_RE.test(company.website.trim())) e.website = "Enter a valid website";
    }

    if (s.id === "industry") {
      if (!company.industry) e.industry = "Please select your industry";
      if (company.industry === "other" && !company.industryOther.trim()) e.industryOther = "Please tell us your industry";
    }

    if (s.questions) {
      s.questions.forEach(q => {
        const err = isAnswered(q, answers[q.id], otherInputs[q.id]);
        if (err) e[q.id] = err;
      });
    }

    if (s.id === "challenges") {
      const len = company.challenges.trim().length;
      if (len < MIN_CHALLENGE_CHARS) {
        e.challenges = `Please add a little more detail (${MIN_CHALLENGE_CHARS - len} more characters)`;
      }
      if (!turnstileToken) e.turnstile = "Please complete the security check";
    }

    return e;
  }, [company, answers, otherInputs, turnstileToken]);

  const currentErrors = errorsFor(current);
  const firstInvalidStep = useMemo(() => {
    const idx = steps.findIndex(s => Object.keys(errorsFor(s)).length > 0);
    return idx === -1 ? steps.length - 1 : idx;
  }, [steps, errorsFor]);

  // -----------------------------
  // HANDLERS
  // -----------------------------
  const updateCompany = (patch) => setCompany(c => ({ ...c, ...patch }));

  const selectIndustry = (ind) => {
    if (ind.key !== company.industry) {
      // Drop answers to the previous industry's questions; common answers are kept.
      const keep = new Set(questions.filter(q => !q.industry).map(q => q.id));
      setAnswers(a => Object.fromEntries(Object.entries(a).filter(([id]) => keep.has(id))));
      setOtherInputs(o => Object.fromEntries(Object.entries(o).filter(([id]) => keep.has(id))));
    }
    updateCompany({ industry: ind.key, industryLabel: ind.label });
  };

  const goTo = (index) => {
    setShowErrors(false);
    setSubmitError(null);
    setStep(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const next = () => {
    if (Object.keys(currentErrors).length) {
      setShowErrors(true);
      return;
    }
    if (current.id === "industry" && questionsLoading) return;
    goTo(safeStep + 1);
  };

  const back = () => goTo(Math.max(0, safeStep - 1));

  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileKey(k => k + 1);
  };

  const submit = async () => {
    const invalid = steps.findIndex(s => Object.keys(errorsFor(s)).length > 0);
    if (invalid !== -1) {
      if (invalid !== safeStep) goTo(invalid);
      setShowErrors(true);
      return;
    }

    setSubmitError(null);
    setStage("loading");

    const formattedAnswers = questions.map(q => ({
      question_id: q.id,
      key: q.key,
      question: q.question,
      section: q.section === INDUSTRY_SECTION ? `${industryLabel} Focus` : q.section,
      answer: resolveAnswer(q, answers[q.id], otherInputs[q.id])
    }));

    try {
      const res = await API.post("/scan", {
        company: { ...company, industryLabel: company.industry === "other" ? "Other" : company.industryLabel },
        answers: formattedAnswers,
        turnstileToken
      }, { timeout: 300000 });

      setReport(mapAIReport(res.data.report, company));
      setStage("success");
    } catch (err) {
      const message = err.response?.data?.error ||
        (err.code === "ECONNABORTED"
          ? "This is taking longer than expected. Please try again."
          : "We couldn't generate your report. Please check your connection and try again.");
      setSubmitError(message);
      setStage("form");
    } finally {
      // Turnstile tokens are single-use, so always get a fresh one for the next attempt.
      resetTurnstile();
    }
  };

  const startOver = () => {
    clearSaved();
    setCompany(EMPTY_COMPANY);
    setAnswers({});
    setOtherInputs({});
    setReport(null);
    setStep(0);
    setShowErrors(false);
    setSubmitError(null);
    setStage("form");
    window.scrollTo({ top: 0 });
  };

  // -----------------------------
  // STAGES
  // -----------------------------
  if (stage === "loading") return <AILoader industry={industryLabel} />;

  if (waitingForQuestions && stage === "form") {
    return <div className="loader-screen"><p className="empty-note">Restoring your progress…</p></div>;
  }

  if (stage === "success" && report) {
    return <SuccessScreen report={report} company={company} onContinue={() => setStage("report")} />;
  }

  if (stage === "report" && report) {
    return <ReportDashboard report={report} company={{ ...company, industryLabel }} onStartOver={startOver} />;
  }

  const isLast = safeStep === steps.length - 1;
  const answeredCount = questions.filter(q => !isAnswered(q, answers[q.id], otherInputs[q.id])).length;
  const progress = Math.round((safeStep / Math.max(1, steps.length - 1)) * 100);

  return (
    <div className="page">
      <div className={`top-band ${safeStep === 0 ? "with-hero" : ""}`}>
      <div className="container">
        <header className="app-header">
          <img src="./emeis.png" alt="Emeis Technologies" className="logo" />
          <div className="spacer-col" />
          <div className="save-status" title="Your answers are saved in this browser">
            <span className="save-dot" /> Auto-saved
          </div>
        </header>

        {safeStep === 0 && (
          <div className="hero">
            <span className="pill"><Zap size={13} /> AI-powered assessment</span>
            <h1>Find where AI can <span className="grad">grow your business</span></h1>
            <p>Answer a few industry-specific questions and get a personalised AI score, opportunities and a 12-month roadmap in about 5 minutes.</p>
          </div>
        )}

        <div className="stepper-wrap">
          <div className="progress-meta">
            <span>Step <b>{safeStep + 1}</b> of {steps.length}{questions.length > 0 && ` · ${answeredCount}/${questions.length} questions answered`}</span>
            <span>{progress}%</span>
          </div>
          <div className="progress-track">
            <motion.div className="progress-fill" initial={false} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
          <nav className="steps" aria-label="Steps">
            {steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={`step-chip ${i === safeStep ? "active" : ""} ${i < safeStep ? "done" : ""}`}
                disabled={i > firstInvalidStep}
                onClick={() => goTo(i)}
                aria-current={i === safeStep ? "step" : undefined}
              >
                <span className="num">{i < safeStep ? "✓" : i + 1}</span>
                {s.title}
              </button>
            ))}
          </nav>
        </div>
      </div>
      </div>

      <div className="container main">
        <AnimatePresence mode="wait">
          <motion.section
            key={current.id}
            className="card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="card-head">
              <div className="eyebrow">{current.eyebrow}</div>
              <h2>{current.title}</h2>
              <p>{current.subtitle}</p>
            </div>

            {current.id === "contact" && (
              <ContactStep company={company} onChange={updateCompany} errors={currentErrors} showErrors={showErrors} />
            )}

            {current.id === "industry" && (
              <IndustryStep
                industries={industries}
                loading={industriesLoading}
                company={company}
                onSelect={selectIndustry}
                onChange={updateCompany}
                errors={currentErrors}
                showErrors={showErrors}
              />
            )}

            {current.questions && (
              <QuestionsStep
                questions={current.questions}
                startNumber={current.startNumber}
                answers={answers}
                otherInputs={otherInputs}
                onAnswer={(id, v) => setAnswers(a => ({ ...a, [id]: v }))}
                onOther={(id, v) => setOtherInputs(o => ({ ...o, [id]: v }))}
                errors={currentErrors}
                showErrors={showErrors}
              />
            )}

            {current.id === "challenges" && (
              <ChallengesStep
                company={company}
                onChange={updateCompany}
                errors={currentErrors}
                showErrors={showErrors}
                turnstileKey={turnstileKey}
                verified={Boolean(turnstileToken)}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
              />
            )}

            {showErrors && Object.keys(currentErrors).length > 0 && current.questions && (
              <div className="alert alert-error">
                <AlertCircle size={18} />
                Please answer all {Object.keys(currentErrors).length === 1 ? "remaining question" : `${Object.keys(currentErrors).length} remaining questions`} to continue.
              </div>
            )}

            {loadError && (
              <div className="alert alert-error"><AlertCircle size={18} /> {loadError}</div>
            )}

            {submitError && (
              <div className="alert alert-error"><AlertCircle size={18} /> {submitError}</div>
            )}
          </motion.section>
        </AnimatePresence>

        <div className="nav-bar">
          {safeStep > 0 ? (
            <button type="button" className="btn btn-ghost" onClick={back}>
              <ArrowLeft size={17} /> Back
            </button>
          ) : <span />}

          {isLast ? (
            <button type="button" className="btn btn-primary btn-lg" onClick={submit}>
              <Sparkles size={18} /> Generate my AI report
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={next}
              disabled={current.id === "industry" && questionsLoading}
            >
              {current.id === "industry" && questionsLoading ? "Loading questions…" : "Continue"} <ArrowRight size={17} />
            </button>
          )}
        </div>

        {safeStep === 0 && (
          <div className="trust-row">
            <span><Clock size={15} /> Takes about 5 minutes</span>
            <span><Lock size={15} /> Your data stays confidential</span>
            <span><Sparkles size={15} /> {industries.length || 25}+ industries covered</span>
          </div>
        )}
      </div>
    </div>
  );
}
